import { useContext, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import SectionCard from './SectionCard';
import SectionApi from '../../api/sectionApi';
import SkillApi from '../../api/skillApi';
import ButtonLink from '../../components/atoms/anchor/ButtonLink';
import IconButton from '../../components/atoms/button/IconButton';
import { CreationProgressSidebarContent } from '../../components/organisms/sidebar/CreationProgressSidebarContent';
import { SingleTabSidebar } from '../../components/organisms/sidebar/Sidebar';
import HeaderArea from '../../components/page-layout/HeaderArea';
import MainArea from '../../components/page-layout/MainArea';
import PageRightSidebar from '../../components/page-layout/PageRightSidebar';
import SidebarArea from '../../components/page-layout/SidebarArea';
import SectionService from '../../services/sectionService';
import { Breadcrumbs } from '../../types/breadcrumbs';
import { ContentData } from '../../types/contentTypes';
import SectionData from '../../types/SectionData';
import SkillData from '../../types/skillData';
import ModalContext from '../../utils/ModalContext';

import './ManageSections.scss';

function CreateNewSection() {
  const [skill, setSkill] = useState<SkillData>();
  const [sections, setSections] = useState<SectionData[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumbs>([
    [`#`, 'Loading...'],
    ['#', 'Manage Sections'],
  ]);

  const { skillSlug } = useParams<{ skillSlug: string }>();
  const { showConfirmModal } = useContext(ModalContext);

  useEffect(() => {
    async function fetchData() {
      const fetchedSkill = await SkillApi.getSkillBySlug(skillSlug!);
      const fetchedSections = await SectionApi.getSectionsBySkillId(fetchedSkill.id);
      setSkill(fetchedSkill);
      setSections(fetchedSections);
      setBreadcrumbs([
        ['/', 'Home'],
        [`/skills/${fetchedSkill.slug}`, fetchedSkill.name],
        ['#', 'Manage sections'],
      ]);
    }

    fetchData();
  }, [skillSlug]);

  async function handleAdd(skill: SkillData) {
    const newSection = {
      name: '',
      skillId: skill.id,
    };
    const updatedSections = await SectionService.addSectionLastInArray(newSection, sections);
    setSections(updatedSections);
  }

  async function handleDelete(section: SectionData) {
    showConfirmModal(async () => {
      const updatedSections = await SectionService.removeSectionFromArray(section, sections);
      setSections(updatedSections);
    }, `Are you sure you want to delete section${section.name ? ` '${section.name}'` : ''}?`);
  }

  async function handleUpdate(section: SectionData) {
    const updatedSections = await SectionService.updateSectionInArray(section, sections);
    setSections(updatedSections);
  }

  async function handleSwapOrder(sections: SectionData[] | ContentData[], index1: number, index2: number) {
    const newSections = await SectionService.swapSectionsInArray(index1, index2, sections as SectionData[]);
    setSections(newSections);
  }

  const SectionCards =
    skill &&
    sections.map(section => (
      <SectionCard
        key={section.id}
        className="mb-8"
        section={section}
        skill={skill}
        onUpdate={handleUpdate}
        onDelete={() => handleDelete(section)}
      />
    ));

  return (
    <PageRightSidebar className="create-new-section" activeNavlink="my-creations" breadcrumbs={breadcrumbs}>
      <HeaderArea>
        <h1 className="strong-heading">Manage sections</h1>
      </HeaderArea>
      <MainArea>
        {sections.length === 0 && (
          <p className="mb-8">There are no sections for this skill yet. Add one with the button below.</p>
        )}
        {SectionCards}
        <div className="flex">
          {skill && <IconButton className="mr-auto" icon="add" label="Add Section" onClick={() => handleAdd(skill)} />}
          <ButtonLink className="ml-auto" visualType="secondary" to={`/skills/${skillSlug}`}>
            Back to skill
          </ButtonLink>
        </div>
      </MainArea>
      <SidebarArea>
        <SingleTabSidebar
          tabLabel={'Your progress'}
          component={
            skill && (
              <CreationProgressSidebarContent skill={skill} sections={sections} handleSwapOrder={handleSwapOrder} />
            )
          }
        />
      </SidebarArea>
    </PageRightSidebar>
  );
}

export default CreateNewSection;
